import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import "./outside-organization-home.css";

const greetingForHour = (hour) => {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const OutsideOrganizationHome = () => {
  const { user } = useAuth0();

  useEffect(() => {
    document.title = "Home | evoJets HQ";
  }, []);

  const firstName =
    user?.["https://new.evojets.com/userMetadata"]?.first_name?.trim() || "";
  const organizationName =
    user?.["https://new.evojets.com/appMetadata"]?.organizationName?.trim() ||
    "";

  const greeting = greetingForHour(new Date().getHours());

  return (
    <Fragment>
      <section className="partner-home">
        <div className="partner-home__grid">
          <div className="partner-home__lede">
            <p className="partner-home__eyebrow">
              <span className="partner-home__eyebrow-rule" aria-hidden="true" />
              Partner Portal
            </p>

            <h1 className="partner-home__headline">
              {greeting},{" "}
              <span className="partner-home__headline-accent">
                {firstName || "welcome"}
              </span>
              .
            </h1>

            {organizationName && (
              <p className="partner-home__org">
                Signed in on behalf of <strong>{organizationName}</strong>.
              </p>
            )}

            <p className="partner-home__support">
              Price a new charter or pick up where you left off with the trip
              requests you&rsquo;ve already sent to evoJets.
            </p>

            <div className="partner-home__actions">
              <Link
                to="/calculator"
                className="partner-home__btn partner-home__btn--primary"
              >
                <span>Request a flight</span>
                <svg
                  className="partner-home__btn-arrow"
                  viewBox="0 0 20 20"
                  width="16"
                  height="16"
                  aria-hidden="true"
                >
                  <path
                    d="M3 10h14M11 4l6 6-6 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>

              <Link to="/trip-requests" className="partner-home__btn-link">
                View your trip requests
                <svg
                  viewBox="0 0 12 12"
                  width="12"
                  height="12"
                  aria-hidden="true"
                >
                  <path
                    d="M2 6h7M6 2l4 4-4 4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </Link>
            </div>
          </div>

          <aside
            className="partner-home__ghost"
            aria-label="Preview of the quote request flow"
          >
            <div className="partner-home__ghost-body" aria-hidden="true">
              {/* Stage 01 — fill the calculator */}
              <div
                className="partner-home__ghost-stage partner-home__ghost-stage--fill"
                data-stage="1"
              >
                <div className="partner-home__ghost-caption">
                  <span>01 · Get Estimates</span>
                </div>

                <div className="partner-home__ghost-field partner-home__ghost-field--wide">
                  <span className="partner-home__ghost-key">Trip</span>
                  <span className="partner-home__ghost-val">One-way</span>
                </div>

                <div className="partner-home__ghost-row">
                  <div className="partner-home__ghost-field">
                    <span className="partner-home__ghost-key">From</span>
                    <span className="partner-home__ghost-val partner-home__ghost-val--type">
                      KTEB
                    </span>
                  </div>
                  <div className="partner-home__ghost-field">
                    <span className="partner-home__ghost-key">To</span>
                    <span className="partner-home__ghost-val partner-home__ghost-val--type partner-home__ghost-val--type-2">
                      KASE
                    </span>
                  </div>
                </div>

                <div className="partner-home__ghost-row">
                  <div className="partner-home__ghost-field">
                    <span className="partner-home__ghost-key">Pax</span>
                    <span className="partner-home__ghost-val">6</span>
                  </div>
                  <div className="partner-home__ghost-field">
                    <span className="partner-home__ghost-key">Date</span>
                    <span className="partner-home__ghost-val">Jun 14</span>
                  </div>
                </div>

                <div className="partner-home__ghost-action">
                  <span className="partner-home__ghost-button partner-home__ghost-button--pulse">
                    Calculate
                  </span>
                </div>
              </div>

              {/* Stage 02 — review pricing */}
              <div
                className="partner-home__ghost-stage partner-home__ghost-stage--price"
                data-stage="2"
              >
                <div className="partner-home__ghost-caption">
                  <span>02 · Request Trip</span>
                </div>

                <ul className="partner-home__ghost-list">
                  <li style={{ "--i": 0 }}>
                    <span>Super Midsize</span>
                    <span className="partner-home__ghost-price">
                      $38,000 – 48,500
                    </span>
                  </li>
                  <li style={{ "--i": 1 }} data-selected>
                    <span>Heavy Jet</span>
                    <span className="partner-home__ghost-price">
                      $44,500 – 57,000
                    </span>
                  </li>
                  <li style={{ "--i": 2 }}>
                    <span>Ultra Long Range</span>
                    <span className="partner-home__ghost-price">
                      $66,500 – 81,000
                    </span>
                  </li>
                </ul>

                <div className="partner-home__ghost-action">
                  <span className="partner-home__ghost-button partner-home__ghost-button--late">
                    Request this trip
                  </span>
                </div>
              </div>

              {/* Stage 03 — request sent */}
              <div
                className="partner-home__ghost-stage partner-home__ghost-stage--sent"
                data-stage="3"
              >
                <div className="partner-home__ghost-caption">
                  <span>03 · Fulfillment</span>
                </div>

                <div className="partner-home__ghost-confirm">
                  <svg
                    className="partner-home__ghost-check"
                    viewBox="0 0 32 32"
                    width="32"
                    height="32"
                    aria-hidden="true"
                  >
                    <circle
                      cx="16"
                      cy="16"
                      r="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className="partner-home__ghost-check-ring"
                    />
                    <path
                      d="M9 16.5 14 21.5 23 11.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="partner-home__ghost-check-mark"
                    />
                  </svg>

                  <div className="partner-home__ghost-confirm-text">
                    <p>Trip Request Received</p>
                    <p className="partner-home__ghost-confirm-sub">
                      Your Evo rep is on it.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <ol className="partner-home__ghost-pips" aria-hidden="true">
              <li data-pip="1">Get Estimates</li>
              <li data-pip="2">Request Trip</li>
              <li data-pip="3">Fulfillment</li>
            </ol>
          </aside>
        </div>
      </section>
    </Fragment>
  );
};

export default OutsideOrganizationHome;
